// SPDX-License-Identifier: GPL-3.0-only
// JavaScript adaptation of nudat v0.1.1 (MIT), OpenSagaDev, revision
// e329291cc7cb204b709a57d0e2853d48ac43e63f: src/lib.rs and src/lz2k.rs.
// Original archives remain local Blob/File sources; only requested byte ranges
// are read. No archive member is executed or written to the host filesystem.
export function normalizeAssetPath(path) {
  if (typeof path !== 'string') throw new Error('Invalid asset path.');
  const normalized = path.replaceAll('\\', '/');
  if (!normalized || normalized.startsWith('/') || /[:\0]/.test(normalized) || normalized.split('/').some(p => !p || p === '.' || p === '..')) throw new Error('Invalid asset path.');
  return normalized.toLowerCase();
}
export function pathHash(path) {
  let hash = 0x811c9dc5;
  for (const byte of new TextEncoder().encode(path.replaceAll('/', '\\'))) {
    const upper = byte >= 97 && byte <= 122 ? byte - 32 : byte;
    hash = Math.imul(hash ^ upper, 0x199933) >>> 0;
  }
  return hash;
}

class Bits {
  constructor(data) { this.data = data; this.position = 0; }
  get(count) {
    if (!Number.isInteger(count) || count < 0 || count > 24 || this.position + count > this.data.length * 8) throw new Error('Truncated LZ2K bits.');
    let value = 0;
    for (let i = 0; i < count; i++, this.position++) value = (value << 1) | ((this.data[this.position >> 3] >> (7 - (this.position & 7))) & 1);
    return value;
  }
  peek(count) { const before = this.position; const value = this.get(count); this.position = before; return value; }
}
function constant(value) { return { decode: () => value }; }
function huffman(lengths) {
  const counts = new Uint32Array(17), next = new Uint32Array(17), rows = Array.from({ length: 17 }, () => new Map());
  for (const length of lengths) { if (length > 16) throw new Error('Invalid LZ2K Huffman length.'); counts[length]++; }
  let code = 0;
  for (let n = 1; n <= 16; n++) {
    code = (code + (n === 1 ? 0 : counts[n - 1])) << (n === 1 ? 0 : 1);
    if (code + counts[n] > 2 ** n) throw new Error('Oversubscribed LZ2K Huffman tree.');
    next[n] = code;
  }
  if (code + counts[16] !== 65536) throw new Error('Incomplete LZ2K Huffman tree.');
  lengths.forEach((length, symbol) => { if (length) rows[length].set(next[length]++, symbol); });
  return { decode(bits) {
    let code = 0;
    for (let n = 1; n <= 16; n++) { code = (code << 1) | bits.get(1); const symbol = rows[n].get(code); if (symbol !== undefined) return symbol; }
    throw new Error('Invalid LZ2K Huffman code.');
  } };
}
function offsetTable(bits, size, countBits, zeroAfter) {
  const n = bits.get(countBits);
  if (!n) return constant(bits.get(countBits));
  if (n > size) throw new Error('Oversized LZ2K offset table.');
  const lengths = new Uint8Array(size);
  let i = 0;
  while (i < n) {
    let value = bits.peek(3);
    if (value === 7) {
      let offset = 3;
      while (bits.peek(offset + 1) & 1) { value++; if (++offset > 16) throw new Error('LZ2K code length overflow.'); }
      bits.get(offset + 1);
    } else bits.get(3);
    lengths[i++] = value;
    if (i === zeroAfter) { i += bits.get(2); if (i > size) throw new Error('LZ2K offset table overflow.'); }
  }
  return huffman(lengths);
}
function literalTable(bits, lengthsTable) {
  const n = bits.get(9);
  if (!n) return constant(bits.get(9));
  if (n > 510) throw new Error('Oversized LZ2K literal table.');
  const lengths = new Uint8Array(510);
  let i = 0;
  while (i < n) {
    const value = lengthsTable.decode(bits);
    if (value < 3) { i += value === 0 ? 1 : value === 1 ? bits.get(4) + 3 : bits.get(9) + 20; if (i > 510) throw new Error('LZ2K literal table overflow.'); }
    else lengths[i++] = value - 2;
  }
  return huffman(lengths);
}
export function decodeLZ2K(data, outputLength) {
  if (!(data instanceof Uint8Array) || !Number.isSafeInteger(outputLength) || outputLength < 0 || outputLength > 512 * 1024 * 1024) throw new Error('Invalid LZ2K output size.');
  const bits = new Bits(data), output = new Uint8Array(outputLength);
  let written = 0, left = 0, literals = constant(0), offsets = constant(0);
  while (written < output.length) {
    if (!left) {
      left = bits.get(16); if (!left) throw new Error('Empty LZ2K block.');
      literals = literalTable(bits, offsetTable(bits, 19, 5, 3)); offsets = offsetTable(bits, 14, 4, -1);
    }
    left--;
    const symbol = literals.decode(bits);
    if (symbol < 256) output[written++] = symbol;
    else {
      const code = offsets.decode(bits);
      const distance = (code === 0 ? 0 : 2 ** (code - 1) + bits.get(code - 1)) + 1, count = symbol - 0xfd;
      if (distance > written || count > output.length - written) throw new Error('Invalid LZ2K back-reference.');
      for (let i = 0; i < count; i++) { output[written] = output[written - distance]; written++; }
    }
  }
  return output;
}

export function parseDatIndex(bytes, indexOffset, archiveSize) {
  if (!(bytes instanceof Uint8Array) || bytes.byteLength > 64 * 1024 * 1024 || !Number.isSafeInteger(indexOffset) || indexOffset < 8 || !Number.isSafeInteger(archiveSize) || indexOffset + bytes.length !== archiveSize) throw new Error('Invalid DAT index bounds.');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let cursor = 0;
  const range = (n) => { if (!Number.isSafeInteger(n) || n < 0 || cursor + n > bytes.length) throw new Error('Truncated DAT index.'); const at = cursor; cursor += n; return at; };
  const i32 = () => view.getInt32(range(4), true), u32 = () => view.getUint32(range(4), true), i16 = () => view.getInt16(range(2), true);
  const version = i32(); if (version !== -3 && version !== -2) throw new Error('Unsupported DAT version; choose original PC archives.');
  const count = i32(); if (count < 0 || count > 1000000 || count > (bytes.length - cursor) / 16) throw new Error('Invalid DAT file count.');
  const infos = Array.from({ length: count }, () => {
    const offset = i32() * 256, storedSize = i32(), size = i32(), compression = i32();
    if (offset < 0 || storedSize < 0 || size < 0 || offset + storedSize > indexOffset || ![0, 2].includes(compression)) throw new Error('Invalid DAT entry range/compression.');
    if (!compression && storedSize !== size) throw new Error('Uncompressed DAT size mismatch.');
    return { offset, storedSize, size, compression };
  });
  const nodeCount = i32(); if (nodeCount < 1 || nodeCount > 32767) throw new Error('Invalid DAT node count.');
  const nodes = Array.from({ length: nodeCount }, () => ({ child: i16(), sibling: i16(), nameOffset: u32() }));
  const nameLength = i32(), nameStart = range(nameLength), decoder = new TextDecoder('utf-8', { fatal: true });
  for (const node of nodes) {
    if (node.nameOffset >= nameLength) throw new Error('Invalid DAT name offset.');
    const start = nameStart + node.nameOffset, end = bytes.indexOf(0, start);
    if (end < start || end >= nameStart + nameLength) throw new Error('Unterminated DAT name.');
    node.name = decoder.decode(bytes.subarray(start, end));
  }
  const hashes = Array.from({ length: count }, () => u32());
  if (version === -3 && hashes.some((hash, i) => i > 0 && hashes[i - 1] >= hash)) throw new Error('Unsorted/colliding DAT hash table.');
  const hashCount = i32(), hashNamesLength = i32(); if (hashCount < 0) throw new Error('Invalid DAT hash count.'); range(hashNamesLength);
  if (cursor !== bytes.length) throw new Error('Trailing DAT index data.');
  const paths = new Array(count), seen = new Set(), stack = [{ at: nodes[0].child, parent: '' }];
  while (stack.length) {
    const { at, parent } = stack.pop(); if (!at) continue;
    if (at < 0 || at >= nodes.length || seen.has(at)) throw new Error('Invalid/cyclic DAT tree.');
    seen.add(at); const node = nodes[at];
    if (!node.name || /[/\\:\0]/.test(node.name) || node.name === '.' || node.name === '..') throw new Error('Invalid DAT tree component.');
    const path = parent ? `${parent}/${node.name}` : node.name;
    if (path.length > 4096) throw new Error('DAT path exceeds supported length.');
    stack.push({ at: node.sibling, parent });
    if (node.child <= 0) {
      const index = -node.child; if (index >= count || paths[index] !== undefined) throw new Error('Invalid/duplicate DAT leaf.'); paths[index] = path;
    } else stack.push({ at: node.child, parent: path });
  }
  if (paths.filter(path => path !== undefined).length !== count) throw new Error('DAT tree does not name every entry.');
  const byHash = new Map(hashes.map((hash, i) => [hash, i])), entries = new Map(), assigned = new Set();
  for (let leaf = 0; leaf < count; leaf++) {
    const path = paths[leaf], hash = pathHash(path), index = version === -2 ? leaf : byHash.get(hash);
    if (index === undefined || hashes[index] !== hash || assigned.has(index)) throw new Error('Missing/duplicate DAT path hash.');
    assigned.add(index); const key = normalizeAssetPath(path);
    if (entries.has(key)) throw new Error('Duplicate DAT path.'); entries.set(key, { ...infos[index], path: key });
  }
  return { version, entries, indexOffset };
}

export async function openDat(blob) {
  if (!blob || typeof blob.slice !== 'function' || !Number.isSafeInteger(blob.size) || blob.size < 16) throw new Error('Invalid DAT file.');
  const head = new DataView(await blob.slice(0, 8).arrayBuffer()), raw = head.getInt32(0, true);
  const indexOffset = raw < 0 ? -raw * 256 : raw;
  if (indexOffset < 8 || indexOffset + 8 > blob.size || blob.size - indexOffset > 64 * 1024 * 1024) throw new Error('Invalid DAT index location.');
  const archive = parseDatIndex(new Uint8Array(await blob.slice(indexOffset).arrayBuffer()), indexOffset, blob.size);
  return { ...archive, name: blob.name ?? 'DAT', async read(path) {
    const entry = archive.entries.get(normalizeAssetPath(path)); if (!entry) throw new Error(`Original asset not found: ${path}`);
    if (entry.size > 512 * 1024 * 1024 || entry.storedSize > 512 * 1024 * 1024) throw new Error('Asset exceeds browser read budget.');
    const stored = new Uint8Array(await blob.slice(entry.offset, entry.offset + entry.storedSize).arrayBuffer());
    if (stored.length !== entry.storedSize) throw new Error('Truncated DAT payload.');
    if (!entry.compression) return stored;
    const output = new Uint8Array(entry.size), view = new DataView(stored.buffer);
    let at = 0, written = 0, blocks = 0;
    while (at < stored.length) {
      if (at + 12 > stored.length || stored[at] !== 76 || stored[at + 1] !== 90 || stored[at + 2] !== 50 || stored[at + 3] !== 75) throw new Error('Invalid LZ2K block header.');
      const decoded = view.getUint32(at + 4, true), compressed = view.getUint32(at + 8, true); at += 12;
      if (compressed > stored.length - at || decoded > output.length - written || (!compressed && decoded)) throw new Error('Invalid compressed DAT block range.');
      const payload = stored.subarray(at, at + compressed);
      output.set(compressed === decoded ? payload : decodeLZ2K(payload, decoded), written);
      at += compressed; written += decoded;
      if (++blocks % 64 === 0) await new Promise(resolve => setTimeout(resolve, 0));
    }
    if (written !== output.length) throw new Error('DAT decoded size mismatch.');
    return output;
  } };
}

export async function openGameArchives(files) {
  const list = [...files].filter(file => /^(?:GAME|EPISODE_I|EPISODE_II|EPISODE_III|EPISODE_IV|EPISODE_V|EPISODE_VI)\.DAT$/i.test(file.name));
  if (!list.some(file => /^GAME\.DAT$/i.test(file.name))) throw new Error('Select the Steam game folder containing GAME.DAT.');
  const archives = [], entries = new Map(), archiveNames = new Set();
  for (const file of list) {
    const name = file.name.toLowerCase(); if (archiveNames.has(name)) throw new Error('Duplicate original archive name.'); archiveNames.add(name);
    const archive = await openDat(file); archives.push(archive);
    for (const [path, entry] of archive.entries) {
      if (entries.has(path)) throw new Error(`Duplicate asset across original archives: ${path}`);
      entries.set(path, { ...entry, archive });
    }
  }
  return { archives, entries, has: path => entries.has(normalizeAssetPath(path)), async read(path) {
    const entry = entries.get(normalizeAssetPath(path)); if (!entry) throw new Error(`Original asset not found: ${path}`);
    return entry.archive.read(path);
  }, async text(path) { return new TextDecoder('utf-8', { fatal: true }).decode(await this.read(path)); } };
}
